import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { getWedding, getWeddingGuestStats, getWeddingGuests } from '../../../lib/api';
import { getTemplate } from '../../../config/templates';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const AnalyticsTab = ({ weddingId }) => {
  const { token } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [recentGuests, setRecentGuests] = useState([]);
  const [guestWishes, setGuestWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token || !weddingId) return;
    try {
      const [statsData, weddingData, guestData] = await Promise.all([
        getWeddingGuestStats(token, weddingId),
        getWedding(token, weddingId),
        getWeddingGuests(token, weddingId),
      ]);
      setStats(statsData);
      const content = typeof weddingData.wedding.content === 'string'
        ? JSON.parse(weddingData.wedding.content)
        : weddingData.wedding.content;
      setWedding({ ...weddingData.wedding, content });

      const guests = guestData.guests || [];
      const sorted = [...guests].sort((a, b) =>
        new Date(b.submitted_at || b.created_at || 0) - new Date(a.submitted_at || a.created_at || 0)
      );
      setRecentGuests(sorted.slice(0, 5));
      setGuestWishes(guests.filter(g => g.special_requests?.trim()));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, weddingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <p>{error}</p>
        <button onClick={fetchData} className="mt-2 underline text-sm">Retry</button>
      </div>
    );
  }

  const total = stats?.total || 0;
  const confirmed = stats?.confirmed || 0;
  const pending = stats?.pending || 0;
  const declined = total - confirmed - pending;
  const checkedIn = stats?.checked_in || 0;
  const ceremony = stats?.attending_ceremony || 0;
  const reception = stats?.attending_reception || 0;

  const rsvpData = [
    { name: 'Confirmed', value: confirmed, color: '#10B981' },
    { name: 'Pending', value: pending, color: '#F59E0B' },
    { name: 'Declined', value: declined > 0 ? declined : 0, color: '#EF4444' },
  ].filter(d => d.value > 0);

  const templateId = wedding?.content?.selectedTemplate || 'timeless';
  const template = getTemplate(templateId);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground">Wedding Analytics</h2>
        <p className="text-sm md:text-base text-muted-foreground mt-1">
          RSVP stats and guest insights for this wedding
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Users" size={20} color="var(--color-primary)" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Total Guests</h3>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-foreground">{total}</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Confirmed</h3>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-foreground">{confirmed}</p>
          {total > 0 && (
            <p className="text-xs text-muted-foreground mt-1">{Math.round(confirmed / total * 100)}% of total</p>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="Clock" size={20} color="var(--color-warning)" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Pending</h3>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-foreground">{pending}</p>
          {total > 0 && (
            <p className="text-xs text-muted-foreground mt-1">{Math.round(pending / total * 100)}% of total</p>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Icon name="QrCode" size={20} color="var(--color-accent)" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Checked In</h3>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-foreground">{checkedIn}</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* RSVP Breakdown */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">RSVP Breakdown</h3>
          {rsvpData.length > 0 ? (
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rsvpData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name} (${value})`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    {rsvpData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No RSVP data yet
            </div>
          )}
        </div>

        {/* Attendance Breakdown */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">Attendance Details</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Icon name="Church" size={20} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Ceremony</span>
              </div>
              <span className="text-2xl font-bold text-foreground">{ceremony}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Icon name="UtensilsCrossed" size={20} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Reception</span>
              </div>
              <span className="text-2xl font-bold text-foreground">{reception}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Icon name="Gift" size={20} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Gifts Received</span>
              </div>
              <span className="text-2xl font-bold text-foreground">{stats?.gifts_received || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Wedding Info */}
        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">Wedding Info</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground">Template</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: template?.theme?.colors?.primary || '#E91E63' }} />
                <p className="text-sm font-medium text-foreground">{template?.name || templateId}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Slug</p>
              <p className="text-sm text-foreground font-mono">/w/{wedding?.slug || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Created</p>
              <p className="text-sm text-foreground">{formatDate(wedding?.created_at)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Status</p>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                wedding?.is_published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {wedding?.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
          </div>
        </div>

        {/* Recent RSVPs */}
        <div className="lg:col-span-2 bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">Recent RSVPs</h3>
          {recentGuests.length > 0 ? (
            <div className="space-y-3">
              {recentGuests.map((guest) => (
                <div
                  key={guest.id}
                  className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    guest.status === 'confirmed' ? 'bg-success/10 text-success'
                      : guest.status === 'declined' ? 'bg-red-100 text-red-600'
                      : 'bg-warning/10 text-warning'
                  }`}>
                    <Icon name={guest.status === 'confirmed' ? 'CheckCircle' : guest.status === 'declined' ? 'XCircle' : 'Clock'} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm md:text-base text-foreground">
                      <span className="font-medium">{guest.guest_name}</span>
                      {' '}
                      <span className="text-muted-foreground">
                        {guest.status === 'confirmed' ? 'confirmed attendance' : guest.status === 'declined' ? 'declined' : 'RSVP pending'}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {timeAgo(guest.submitted_at || guest.created_at)}
                      {guest.number_of_guests > 1 && ` · ${guest.number_of_guests} guests`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Users" size={32} className="mx-auto mb-2 opacity-50" />
              <p>No RSVPs yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Wedding Wishes */}
      {guestWishes.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-romantic-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground">Wedding Wishes</h3>
              <p className="text-xs text-muted-foreground">{guestWishes.length} message{guestWishes.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {guestWishes.map((guest) => (
              <div key={guest.id} className="p-3 bg-background rounded-lg border border-border">
                <p className="text-sm text-foreground italic mb-2">"{guest.special_requests}"</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-primary">{guest.guest_name}</span>
                  <span className="text-xs text-muted-foreground">{timeAgo(guest.submitted_at || guest.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
